import React, { useState, useMemo, useEffect, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { useTheme } from "../../context/ThemeContext";
import {
  getCustomersinChunk,
  getGroups,
} from "../../reducers/customerTableSlice";
import { getIds } from "../../utils/shareUtils";

const reminderOptions = [
  { label: "10 min", value: "10" },
  { label: "30 min", value: "30" },
  { label: "1 hour", value: "60" },
  { label: "1 day", value: "1440" },
];

export default function Step1MeetingDetails({ defaultValues = {}, onNext }) {
  const toastRef = useRef(null);
  const { control, handleSubmit, formState } = useFormContext();
  const { errors } = formState;
  const { theme } = useTheme();
  const dispatch = useDispatch();

  const { customerData, groups, totalCustomerCount, totalGroupCount } =
    useSelector((s) => s.customerTable);

  const [tab, setTab] = useState("individual");
  const [search, setSearch] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState(
    defaultValues.selectedCustomers || []
  );
  const [selectedGroups, setSelectedGroups] = useState(
    defaultValues.selectedGroups || []
  );

  useEffect(() => {
    if (customerData.length < totalCustomerCount || customerData.length === 0) {
      const fetchAllChunks = async () => {
        let page = 0;
        let done = false;
        while (!done) {
          const result = await dispatch(
            getCustomersinChunk({
              page,
              limit: 100,
              globalFilterValue: "",
            })
          );
          page++;
          const receivedCount = result.payload?.rows?.length || 0;
          if (receivedCount < 100) done = true;
        }
      };
      fetchAllChunks();
    }
  }, [dispatch, customerData.length, totalCustomerCount]);

  useEffect(() => {
    if (!groups.length && totalGroupCount === 0) {
      dispatch(getGroups({ page: 0, limit: 1000, globalFilterValue: "" }));
    }
  }, [dispatch, groups.length, totalGroupCount]);

  const filteredCustomers = useMemo(
    () =>
      customerData.filter(
        (c) =>
          c.name?.toLowerCase().includes(search.toLowerCase()) ||
          c.phone?.toString().includes(search)
      ),
    [customerData, search]
  );

  const filteredGroups = useMemo(
    () =>
      groups.filter((g) =>
        g.groupName?.toLowerCase().includes(search.toLowerCase())
      ),
    [groups, search]
  );

  const toggleCustomer = (cust) =>
    setSelectedCustomers((prev) =>
      prev.find((x) => x.id === cust.id)
        ? prev.filter((x) => x.id !== cust.id)
        : [...prev, cust]
    );

  const toggleGroup = (grp) =>
    setSelectedGroups((prev) =>
      prev.find((x) => x.id === grp.id)
        ? prev.filter((x) => x.id !== grp.id)
        : [...prev, grp]
    );

  const submit = (data) => {
    if (selectedCustomers.length === 0 && selectedGroups.length === 0) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select at least one customer or group.",
      });
      return;
    }

    const selectedCustomerIds = selectedCustomers.length
      ? getIds(selectedCustomers)
      : [];
    const groupIds = selectedGroups.length
      ? selectedGroups.map((g) => g.id)
      : [];

    // 🔁 Like in CampaignCustomer, structure final payload cleanly
    const finalData = {
      ...data,
      selectedCustomers,
      selectedGroups,
      groupIds,
      customerIds: selectedCustomerIds,
    };

    onNext(finalData); // 🚀 pass it to parent stepper logic
  };

  return (
    <>
      <Toast ref={toastRef} />
      <form
        onSubmit={handleSubmit(submit)}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          marginRight: "40rem",
        }}
      >
        {/* ───── Meeting Fields ───── */}
        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label>Meeting title*</label>
            <Controller
              name="title"
              control={control}
              defaultValue={defaultValues.title || ""}
              rules={{ required: "Meeting title is required" }}
              render={({ field }) => (
                <>
                  <InputText
                    {...field}
                    placeholder="e.g. Product Demo"
                    className={errors.title && "p-invalid"}
                  />
                  {errors.title && (
                    <small className="p-error">{errors.title.message}</small>
                  )}
                </>
              )}
            />
          </div>

          {[
            ["date", "Date*", <Calendar showIcon dateFormat="dd/mm/yy" />],
            ["time", "Time*", <Calendar timeOnly hourFormat="12" showIcon />],
            ["organizer", "Organizer*", <InputText placeholder="Enter" />],
            [
              "reminder",
              "Reminder Time*",
              <Dropdown options={reminderOptions} placeholder="30 min" />,
            ],
          ].map(([n, lbl, comp]) => (
            <div key={n} className="field col-6">
              <label>{lbl}</label>
              <Controller
                name={n}
                control={control}
                defaultValue={defaultValues[n] || ""}
                rules={{ required: `${lbl.replace("*", "")} is required` }}
                render={({ field }) => {
                  const isDate = n === "date";
                  const isTime = n === "time";

                  const parseValue = (val) => {
                    if (!val) return "";
                    if (isDate) {
                      // From ISO string or raw Date
                      const date = new Date(val);
                      return new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      );
                    }
                    if (isTime) {
                      // Convert "HH:mm" to Date object for display
                      const [h, m] = val.split(":");
                      const date = new Date();
                      date.setHours(+h);
                      date.setMinutes(+m);
                      date.setSeconds(0);
                      return date;
                    }
                    return val;
                  };

                  return (
                    <>
                      {React.cloneElement(comp, {
                        ...field,
                        value: parseValue(field.value),
                        onChange: (e) => {
                          let val = e.value ?? e.target?.value ?? "";

                          if (isDate && val) {
                            // Keep only the local date, without timezone shift
                            const dateObj = new Date(val);
                            val = `${dateObj.getFullYear()}-${String(
                              dateObj.getMonth() + 1
                            ).padStart(2, "0")}-${String(
                              dateObj.getDate()
                            ).padStart(2, "0")}`;
                          } else if (isTime && val) {
                            val = new Date(val).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            }); // HH:mm
                          }

                          field.onChange(val);
                        },
                        className: errors[n] ? "p-invalid" : "",
                        placeholder: isDate
                          ? "Select Date"
                          : isTime
                          ? "Select Time"
                          : "",
                        showTime: isDate ? false : undefined,
                        hourFormat: isTime ? "24" : undefined,
                        timeOnly: isTime || undefined,
                      })}
                      {errors[n] && (
                        <small className="p-error">{errors[n].message}</small>
                      )}
                    </>
                  );
                }}
              />
            </div>
          ))}

          <div className="field col-12">
            <label>Meeting URL*</label>
            <Controller
              name="url"
              control={control}
              defaultValue={defaultValues.url || ""}
              render={({ field }) => (
                <InputText
                  {...field}
                  placeholder="https://meet.google.com/..."
                />
              )}
            />
          </div>
        </div>

        <Divider />

        {/* ───── Participant Selection ───── */}
        <div>
          <h4>Select Participants</h4>
          <small style={{ color: theme.text_secondary }}>
            Choose individual customers or groups for this meeting
          </small>

          <div
            style={{
              display: "flex",
              background: theme.sideBarText,
              marginTop: "1rem",
              padding: "0.5rem 0.75rem",
              borderRadius: 6,
              border: `1px solid ${theme.border}`,
              alignItems: "center",
            }}
          >
            <i className="pi pi-search" style={{ marginRight: 6 }} />
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers or groups"
              style={{ border: "none", flex: 1 }}
            />
          </div>

          <div
            style={{
              display: "flex",
              marginTop: "1rem",
              background: theme.secondary,
              borderRadius: 8,
              border: `1px solid ${theme.primary}`,
              overflow: "hidden",
            }}
          >
            {[
              { k: "individual", lbl: "Individual Customers" },
              { k: "groups", lbl: "Groups" },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setTab(t.k)}
                style={{
                  flex: 1,
                  padding: "0.6rem 1rem",
                  background:
                    tab === t.k ? theme.secondary : theme.base_background,
                  color: tab === t.k ? theme.primary : theme.text_primary,
                  fontWeight: tab === t.k ? 600 : 500,
                  border: "none",
                }}
              >
                {t.lbl}
              </button>
            ))}
          </div>

          <div
            style={{
              marginTop: "1rem",
              maxHeight: 240,
              overflowY: "auto",
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
            }}
          >
            {tab === "individual"
              ? filteredCustomers.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0.75rem 0.9rem",
                      borderBottom: `1px solid ${theme.border}`,
                    }}
                  >
                    <Checkbox
                      binary
                      checked={selectedCustomers.some((x) => x.id === c.id)}
                      onChange={() => toggleCustomer(c)}
                      style={{ marginRight: 12 }}
                    />
                    <label style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{c.name}</div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: theme.text_secondary,
                        }}
                      >
                        {c.phone}
                      </div>
                    </label>
                  </div>
                ))
              : filteredGroups.map((g) => (
                  <div
                    key={g.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0.75rem 0.9rem",
                      borderBottom: `1px solid ${theme.border}`,
                    }}
                  >
                    <Checkbox
                      binary
                      checked={selectedGroups.some((x) => x.id === g.id)}
                      onChange={() => toggleGroup(g)}
                      style={{ marginRight: 12 }}
                    />
                    <label style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{g.groupName}</div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: theme.text_secondary,
                        }}
                      >
                        {g.customerIds?.length || 0} members
                      </div>
                    </label>
                  </div>
                ))}
          </div>
        </div>
      </form>
    </>
  );
}
